<script setup>
import AvatarImageLoader from "@/components/loaders/AvatarImageLoader.vue";
import { useAuthService } from "~/composables/useAuthService";

// Props
const props = defineProps({
  club: {
    type: Object,
    default: null,
  },
});

const { convertAddress } = useCrudConverters();
const { isAuthenticated, isMemberOfClub, isAdminOfClub } = useAuthService();

// Crest initials fallback (mockups/redesign/clubs.html .crest-lg)
const initials = computed(() =>
  (props.club?.name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase(),
);

// "142 members · Manchester" — members + city. (No sport on the clubs API.)
const meta = computed(() => {
  const parts = [];
  if (props.club?.playerCount != null) parts.push(`${props.club.playerCount} members`);
  if (props.club?.city) parts.push(props.club.city);
  else if (props.club?.hasFixedAddress) parts.push(convertAddress(props.club));
  return parts.join(' · ');
});

// Only chips the API actually backs: the viewer's relationship to the club.
const isAdmin = computed(() => isAuthenticated.value && isAdminOfClub(props.club?.id));
const isMember = computed(() => isAuthenticated.value && isMemberOfClub(props.club?.id));
</script>

<template>
  <router-link
      class="club-card tl-card"
      :to="{ name: 'ClubDetail', params: { clubId: club.slug } }"
  >
    <div class="club-card-top">
      <avatar-image-loader
          v-if="club?.imageFileName"
          :image-file-name="club.imageFileName"
          :image-clickable="false"
          size="48"
          class="club-card-crest flex-shrink-0"
      />
      <div v-else class="club-card-crest club-card-crest--fallback flex-shrink-0 d-flex align-center justify-center">
        {{ initials }}
      </div>
      <div class="club-card-who">
        <div class="club-card-title">{{ club.name }}</div>
        <div class="club-card-meta">{{ meta }}</div>
      </div>
    </div>

    <div v-if="isAdmin || isMember" class="club-card-chips">
      <span v-if="isAdmin" class="tl-chip tl-chip--brand">Admin</span>
      <span v-else-if="isMember" class="tl-chip tl-chip--brand">Member</span>
    </div>

    <div class="club-card-foot">
      <span class="club-card-cta">View club</span>
    </div>
  </router-link>
</template>

<style scoped lang="scss">
/* Club card — mockups/redesign/clubs.html .club-card */
.club-card {
  display: block;
  padding: 16px;
  text-decoration: none;
  color: rgb(var(--v-theme-text));
  transition: border-color 0.15s ease;

  &:hover {
    border-color: rgb(var(--v-theme-textSubtle));

    .club-card-cta {
      background: rgb(var(--v-theme-primary));
      color: #fff;
      border-color: rgb(var(--v-theme-primary));
    }
  }
}

.club-card-top {
  display: flex;
  align-items: center;
  gap: 12px;
}

.club-card-crest {
  border-radius: 12px !important;
  width: 48px;
  height: 48px;
}

.club-card-crest--fallback {
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
  font-size: 15px;
  font-weight: 800;
}

.club-card-who {
  min-width: 0;
}

.club-card-title {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.2px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.club-card-meta {
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 2px;
}

.club-card-chips {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 14px;
}

.club-card-foot {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}

/* full-width ghost "View club" button (mockup .btn-small.ghost) */
.club-card-cta {
  display: block;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  padding: 9px 14px;
  border-radius: 999px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  color: rgb(var(--v-theme-text));
  transition: all 0.15s ease;
}
</style>
